/**
 * Исполнитель команд `ui_*` в браузере (ADR-0011).
 *
 * Команду строит и ПРОВЕРЯЕТ сервер (страница, фильтры, блок — по реестру),
 * сюда приходит готовое: `{action: 'navigate', href, highlight}`. Браузер
 * адресов не собирает и непроверенного не исполняет; записи в канон здесь
 * нет — только вид экрана.
 *
 * Что на экране, исполнитель читает из разметки `data-ai-region` /
 * `data-guide` (её же проверяет линтер реестра) — и возвращает модели
 * короткий текст блоков, чтобы она говорила о том, что человек видит.
 */

import registry from './registry.export.json';
import { surfaceText } from './surfaceText';
import './ai-surface.css';

export interface UiCommand {
  action: 'navigate' | 'set_filters' | 'highlight';
  page?: string | null;
  title?: string;
  href?: string;
  highlight?: string | null;
  region?: string | null;
  note?: string | null;
  filters?: Record<string, string>;
}

export interface RegionOnScreen {
  id: string;
  title: string;
  text: string;
}

const REGION_SELECTOR = '[data-ai-region], [data-guide]';
const HIGHLIGHT_MS = 9000;

interface RegistryPage {
  id: string;
  path: string;
  title: string;
  regions: { id: string; title: string }[];
}

export const PAGES: RegistryPage[] = (registry as { pages: RegistryPage[] }).pages;

const regionId = (el: Element): string =>
  el.getAttribute('data-ai-region') ?? el.getAttribute('data-guide') ?? '';

function squeeze(text: string, limit: number): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length > limit ? `${flat.slice(0, limit)}…` : flat;
}

export function pageForPath(pathname: string): RegistryPage | null {
  const candidates = PAGES.filter((page) => {
    if (page.path === '*') return false;
    const pattern = new RegExp(`^${page.path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/:[A-Za-z0-9_]+/g, '[^/]+')}/?$`);
    return pattern.test(pathname);
  });
  return candidates.sort((a, b) => (a.path.match(/:/g)?.length ?? 0) - (b.path.match(/:/g)?.length ?? 0))[0] ?? null;
}

/** Блоки текущего экрана: id, подпись из реестра, видимый текст (сжатый). */
export function regionsOnScreen(limit = 12): RegionOnScreen[] {
  if (typeof document === 'undefined') return [];
  const page = pageForPath(window.location.pathname);
  const titles = new Map(page?.regions.map((r) => [r.id, r.title]) ?? []);
  const seen = new Set<string>();
  const out: RegionOnScreen[] = [];
  for (const el of document.querySelectorAll(`main ${REGION_SELECTOR}, #content ${REGION_SELECTOR}`)) {
    const id = regionId(el);
    if (!id || seen.has(id) || el.closest('.agent-panel')) continue;
    seen.add(id);
    out.push({ id, title: titles.get(id) ?? id, text: squeeze((el as HTMLElement).innerText ?? el.textContent ?? '', 400) });
    if (out.length >= limit) break;
  }
  return out;
}

function findRegion(id: string): HTMLElement | null {
  const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id;
  return document.querySelector<HTMLElement>(`[data-ai-region="${escaped}"], [data-guide="${escaped}"]`);
}

/** Снять всю подсветку гида (и закреплённую шагом тура). */
export function clearHighlights(): void {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('.ai-hl').forEach((el) => {
    el.classList.remove('ai-hl', 'ai-hl--focus', 'ai-hl--sticky');
    el.removeAttribute('data-ai-note');
  });
}

let releaseTimer: number | undefined;

/** Подсветить блок: прокрутка, рамка и подпись; снимается сама. */
export function highlightRegion(id: string, note?: string | null): { ok: boolean; region: string; text?: string; available?: string[] } {
  const el = findRegion(id);
  if (!el) return { ok: false, region: id, available: regionsOnScreen().map((r) => r.id) };
  clearHighlights();
  if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.add('ai-hl');
  if (note) el.setAttribute('data-ai-note', note);
  window.clearTimeout(releaseTimer);
  releaseTimer = window.setTimeout(() => {
    el.classList.remove('ai-hl');
    el.removeAttribute('data-ai-note');
  }, HIGHLIGHT_MS);
  return { ok: true, region: id, text: squeeze(el.innerText ?? el.textContent ?? '', 600) };
}

/**
 * Подсветка шага тура: несколько блоков сразу, рамка держится, пока шаг
 * открыт (снимает следующий шаг или `clearHighlights`). Один блок — ещё и
 * затемнение вокруг. Прокрутка — к первому найденному.
 */
export function highlightRegions(
  ids: string[],
  notes: Record<string, string> = {},
): { shown: string[]; missing: string[] } {
  window.clearTimeout(releaseTimer);
  clearHighlights();
  const shown: string[] = [];
  const missing: string[] = [];
  for (const id of ids) {
    const el = findRegion(id);
    if (!el) {
      missing.push(id);
      continue;
    }
    if (!shown.length && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('ai-hl', 'ai-hl--sticky');
    const note = notes[id];
    if (note) el.setAttribute('data-ai-note', note);
    shown.push(id);
  }
  if (shown.length === 1) findRegion(shown[0] ?? '')?.classList.add('ai-hl--focus');
  return { shown, missing };
}

/** Шаг тура: адрес и блоки — уже проверены сервером по реестру. */
export interface TourStepTarget {
  href: string;
  regions: { id: string; title: string }[];
}

/** Открыть шаг тура по нажатию участника: переход, ожидание отрисовки, подсветка. */
export async function openTourStep(
  step: TourStepTarget,
  navigate: (href: string) => void,
): Promise<{ shown: string[]; missing: string[] }> {
  const href = withSliceParams(step.href);
  const pathname = href.split('?')[0] ?? href;
  const here = `${window.location.pathname}${window.location.search}`;
  if (here !== href) navigate(href);
  await settle(pathname, step.regions[0]?.id ?? null);
  // Подпись рамки — название блока на языке интерфейса (реестр пишет его по-русски для модели).
  return highlightRegions(
    step.regions.map((r) => r.id),
    Object.fromEntries(step.regions.map((r) => [r.id, surfaceText('title', r.title)])),
  );
}

const sleep = (ms: number) => new Promise((done) => window.setTimeout(done, ms));

/** Дождаться, пока экран по адресу отрисуется (блоки перестанут меняться). */
async function settle(pathname: string, wantRegion?: string | null, timeout = 4000): Promise<void> {
  const started = Date.now();
  let last = -1;
  let stable = 0;
  while (Date.now() - started < timeout) {
    await sleep(150);
    if (window.location.pathname !== pathname) continue;
    if (wantRegion && findRegion(wantRegion)) return;
    const count = document.querySelectorAll(REGION_SELECTOR).length + (document.querySelector('main')?.textContent?.length ?? 0);
    stable = count === last ? stable + 1 : 0;
    last = count;
    if (stable >= 3 && !wantRegion) return;
  }
}

/**
 * Дата среза в адресах сервера называется `date` (реестр экранов), а экраны
 * канона читают её как `as_of` — единую дату продукта (`analysis/context`).
 * Ссылка, пришедшая с одним `date`, открывала экран «на сегодня»: ссылка из
 * отчёта «на 20.09» вела в другой срез. Здесь дата копируется в `as_of`;
 * `date` остаётся — его читают экраны на фикстурах и контекст панели.
 */
export function withSliceParams(href: string): string {
  const at = href.indexOf('?');
  if (at < 0) return href;
  const pairs = new URLSearchParams(href.slice(at + 1));
  const date = pairs.get('date');
  if (!date || pairs.has('as_of')) return href;
  pairs.set('as_of', date);
  const text = pairs.toString().replace(/%3A/gi, ':');
  return `${href.slice(0, at)}?${text}`;
}

/** Исполнить проверенную сервером команду. `navigate` — переход роутера. */
export async function runCommand(command: UiCommand, navigate: (href: string) => void): Promise<Record<string, unknown>> {
  if ((command.action === 'navigate' || command.action === 'set_filters') && command.href) {
    const href = withSliceParams(command.href);
    navigate(href);
    const pathname = href.split('?')[0] ?? href;
    const target = command.action === 'navigate' ? command.highlight : null;
    await settle(pathname, target);
    const highlighted = target ? highlightRegion(target, command.note) : null;
    return {
      ok: true,
      url: href,
      page: command.page ?? null,
      title: command.title ?? document.title,
      highlighted: highlighted?.ok ? highlighted.region : null,
      regions_on_screen: regionsOnScreen(),
    };
  }
  if (command.action === 'highlight' && command.region) {
    return highlightRegion(command.region, command.note);
  }
  return { ok: false, error: 'команда не распознана' };
}

/** Ссылка-карточка `ui_suggest`: перейти и подсветить блок. */
export async function openSuggestion(href: string, region: string | null | undefined, navigate: (href: string) => void) {
  return runCommand({ action: 'navigate', href, highlight: region ?? null }, navigate);
}
